/**
 * Vue Router configuration
 * Uses auth composable for route guards instead of sessionStorage
 */

import { useAuth } from '@composables/useAuth';
import { useRoles } from '@composables/useRoles';
import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router';

const Home = () => import('../views/Home.vue');
const RulesDashboard = () => import('../views/rules/RulesDashboard.vue');
const LoanServicingDecisionView = () => import('../views/rules/LoanServicingDecisionView.vue');
const LoanServicingConfigView = () => import('../views/rules/LoanServicingConfigView.vue');
const LoanOffersDecisionView = () => import('../views/rules/LoanOffersDecisionView.vue');
const LoanOffersConfigView = () => import('../views/rules/LoanOffersConfigView.vue');
const RuleEdit = () => import('../views/rules/RuleEditView.vue');
const RuleDetail = () => import('../views/rules/RuleDetailView.vue');
const RuleApproval = () => import('../views/rules/RuleApprovalView.vue');
const RuleView = () => import('../views/rules/RuleView.vue');
const ConfigDashboard = () => import('../views/configs/ConfigDashboard.vue');
const ConfigCreate = () => import('../views/configs/ConfigCreateView.vue');
const ConfigEdit = () => import('../views/configs/ConfigEditView.vue');
const ConfigDetail = () => import('../views/configs/ConfigDetailView.vue');
const ConfigApproval = () => import('../views/configs/ConfigApprovalView.vue');
const TenantsDashboardView = () => import('../views/admin/TenantsDashboardView.vue');
const UsersDashboardView = () => import('../views/admin/UsersDashboardView.vue');
const ThemeConfigView = () => import('../views/admin/ThemeConfigView.vue');
const ViewImageStore = () => import('../views/doc-gen/ViewImageStore.vue');
const ImageView = () => import('../views/doc-gen/ImageView.vue');
const UploadImage = () => import('../views/doc-gen/UploadImage.vue');
const ViewDocLogs = () => import('../views/doc-gen/ViewDocLogs.vue');
const ViewLog = () => import('../views/doc-gen/ViewLog.vue');
const SnackbarTest = () => import('../views/SnackbarTest.vue');
const NotFound = () => import('../views/NotFound.vue');
const QuantScrubIFrameTesting = () => import('../views/QuantScrubIFrameTesting.vue');

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
  },
  {
    path: '/login',
    name: 'Login',
    component: Home,
  },
  {
    path: '/rules',
    children: [
      {
        path: '',
        redirect: { name: 'RulesDashboard', params: { type: 'all' } },
      },
      {
        path: 'loan-servicing',
        name: 'LoanServicingDecision',
        component: LoanServicingDecisionView,
      },
      {
        path: 'loan-servicing-config',
        name: 'LoanServicingConfig',
        component: LoanServicingConfigView,
      },
      {
        path: 'loan-offers',
        name: 'LoanOffersDecision',
        component: LoanOffersDecisionView,
      },
      {
        path: 'loan-offers-config',
        name: 'LoanOffersConfig',
        component: LoanOffersConfigView,
      },
      {
        path: ':type',
        name: 'RulesDashboard',
        component: RulesDashboard,
        props: true,
      },
      {
        path: 'create',
        name: 'RuleCreate',
        component: RuleEdit,
        props: { mode: 'create' },
      },
      {
        path: ':id/edit',
        name: 'RuleEdit',
        component: RuleEdit,
        props: { mode: 'edit' },
      },
      {
        path: ':id',
        name: 'RuleDetail',
        component: RuleDetail,
        props: true,
      },
      {
        path: ':id/view/:type',
        name: 'RuleView',
        component: RuleView,
        props: true,
      },
      {
        path: ':id/approve',
        name: 'RuleApproval',
        component: RuleApproval,
        props: true,
      },
    ],
  },
  {
    path: '/configs',
    children: [
      {
        path: '',
        redirect: { name: 'ConfigDashboard', params: { type: 'all' } },
      },
      {
        path: ':type',
        name: 'ConfigDashboard',
        component: ConfigDashboard,
        props: true,
      },
      {
        path: 'create',
        name: 'ConfigCreate',
        component: ConfigCreate,
        props: true,
      },
      {
        path: ':id/edit/:name/:type',
        name: 'ConfigEdit',
        component: ConfigEdit,
        props: true,
      },
      {
        path: ':id/view/:type',
        name: 'ConfigDetail',
        component: ConfigDetail,
        props: true,
      },
      {
        path: ':id/approve',
        name: 'ConfigApproval',
        component: ConfigApproval,
        props: true,
      },
    ],
  },
  {
    path: '/admin',
    children: [
      {
        path: 'users',
        name: 'UsersDashboard',
        component: UsersDashboardView,
      },
      {
        path: 'tenants',
        name: 'TenantsDashboard',
        component: TenantsDashboardView,
      },
      {
        path: 'branding',
        name: 'BrandingConfig',
        component: ThemeConfigView,
      },
    ],
  },
  {
    path: '/view-image-store',
    name: 'ViewImageStore',
    component: ViewImageStore,
    props: true,
  },
  {
    path: '/image/:id',
    name: 'ImageView',
    component: ImageView,
    props: true,
  },
  {
    path: '/upload-image',
    name: 'UploadImage',
    component: UploadImage,
    props: true,
  },
  {
    path: '/view-doc-logs',
    name: 'ViewDocLogs',
    component: ViewDocLogs,
    props: true,
  },
  {
    path: '/view-log/:id',
    name: 'ViewLog',
    component: ViewLog,
    props: true,
  },
  {
    path: '/quant-scrub-iframe-testing',
    name: 'QuantScrubIFrameTesting',
    component: QuantScrubIFrameTesting,
    props: true,
  },
  {
    path: '/snackbar-test',
    name: 'SnackbarTest',
    component: SnackbarTest,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const publicRoutes = ['/', '/login'];

router.beforeEach(async (to: RouteLocationNormalized) => {
  const { isAuthenticated, fetchAboutMe } = useAuth();

  const routePath = to.path;
  const isPublicRoute = publicRoutes.includes(routePath);

  if (isPublicRoute) {
    return;
  }

  if (!isAuthenticated.value) {
    try {
      await fetchAboutMe();
    } catch {
      return { name: 'Login' };
    }
  }

  if (!isAuthenticated.value) {
    return { name: 'Login' };
  }

  if (to.path.startsWith('/admin') && !useRoles().isAdminUser.value) {
    return { name: 'RulesDashboard', params: { type: 'all' } };
  }
});

export default router;
